import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@goodsteward/gs-ui';
import { useRefreshStripeStatus, type StripeAccountStatus } from '@/api/stripeAccount';

// /settings/stripe/return — Stripe redirects here after the practice
// finishes (or backs out of) the hosted onboarding flow. We call
// /refresh-status once on mount to pick up the post-onboarding state
// without waiting for the account.updated webhook, then point the
// user back at the configuration page.
//
// Phase 0b of docs/build-plans/pma-stripe-payments.md.

export function StripeReturnPage() {
    const refresh = useRefreshStripeStatus();
    const [status, setStatus] = useState<StripeAccountStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fired = useRef(false);

    useEffect(() => {
        if (fired.current) return;
        fired.current = true;
        refresh.mutateAsync()
            .then((result) => setStatus(result))
            .catch((err) => setError(err instanceof Error ? err.message : 'Could not refresh status from Stripe.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ready = !!status?.charges_enabled;
    const submitted = !!status?.details_submitted;

    return (
        <div className="max-w-2xl p-6">
            <h1 className="text-2xl font-semibold text-text mb-3">Back from Stripe</h1>
            {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
            {refresh.isPending && (
                <p className="text-text-subtle mb-4">Refreshing your Stripe status…</p>
            )}
            {!refresh.isPending && status && (
                <div className="bg-surface border border-border rounded-lg p-6 shadow-xs">
                    {ready ? (
                        <p className="text-success-text font-medium">
                            ✓ Your practice is connected and ready to accept invoice payments.
                        </p>
                    ) : submitted ? (
                        <p className="text-warning-text">
                            Stripe has your information and is verifying it.
                            This usually takes a few minutes; sometimes longer.
                            Check back later or wait for the email from Stripe.
                        </p>
                    ) : (
                        <p className="text-warning-text">
                            Onboarding isn't complete yet. You can resume any time
                            from Configuration → Billing.
                        </p>
                    )}
                </div>
            )}
            <p className="mt-6">
                <Link
                    to="/configuration"
                    className="text-accent-text hover:underline"
                >
                    ← Back to billing settings
                </Link>
            </p>
        </div>
    );
}
